import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Icon } from '~/components/atomic';
import { FullScreenLoading, IconButtonWrapper } from '~/components/common';
import { useDeleteAvatarMutation } from '~/features/character';
import { useModal } from '~/hooks';
import { GetListForCreationData, ModalParams } from '~/types';
import { CreateNewCharacterStates } from '~/store/useCreateNewCharacterStore';

interface CharacterListProps {
  selectedId: string;
  handleCharacterClick: (
    s: Pick<CreateNewCharacterStates['preset'], 'id' | 'url'>,
  ) => void;
  characters: GetListForCreationData[];
}

export default function CharacterList({
  selectedId,
  handleCharacterClick,
  characters,
}: CharacterListProps) {
  const { openModal } = useModal();

  const { mutate: deleteMutate, isPending: deletePending } =
    useDeleteAvatarMutation();

  const handleDeleteAvatar = (id: string) => {
    deleteMutate(
      {
        avatar_id: id,
      },
      {
        onSuccess: ({ status }) => {
          if (selectedId === id) {
            handleCharacterClick({
              id: '',
              url: '',
            });
          }
        },
      },
    );
  };

  const openChangePersonaModal = (id: string) => {
    const modalData: ModalParams = {
      title: 'Remove Character',
      content: 'Are you sure you want to remove your avatar?',
      type: 'double_red',
      click: () => handleDeleteAvatar(id),
      buttonName: 'Remove',
    };
    openModal(modalData);
  };

  return (
    <>
      {deletePending && <FullScreenLoading />}
      {characters.map(({ avatar_id, file, thumbnail }) => {
        const selected = selectedId === avatar_id;

        return (
          <CharacterCard
            key={avatar_id}
            onClick={() => handleCharacterClick({ id: avatar_id, url: file })}
            className={selected ? 'on' : ''}
          >
            <img src={thumbnail} alt='preset' />
            <ButtonWrapper
              size={26}
              onClick={(e) => {
                e.stopPropagation();
                openChangePersonaModal(avatar_id);
              }}
            >
              <DeleteButtonWrapper size={16}>
                <Icon iconName='Close' size={16} />
              </DeleteButtonWrapper>
            </ButtonWrapper>
          </CharacterCard>
        );
      })}
    </>
  );
}

export const CharacterCard = styled.div`
  ${({ theme }) => css`
    display: flex;
    position: relative;
    flex: 0 0 auto;
    width: 70px;
    height: 70px;
    border: 1px solid ${theme.stroke.stroke_2};
    border-radius: 50%;
    cursor: pointer;

    img {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
    }

    &.on {
      border: 1px solid ${theme.themeColor.main_1};
    }
  `}
`;

const ButtonWrapper = styled(IconButtonWrapper)`
  position: absolute;
  top: -2px;
  right: -2px;
`;

const DeleteButtonWrapper = styled(IconButtonWrapper)`
  ${({ theme }) => css`
    border-radius: 50%;
    background: ${theme.backgroundColor.bg_light_5_alpha};
    color: ${theme.backgroundColor.bg_light_5_alpha};
  `}
`;
